import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const EFFECTIVE = "February 13, 2026";

function Section({ n, title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-serif text-2xl">
        <span className="text-[#b85c47] mr-2">{n}.</span>
        {title}
      </h2>
      <div className="mt-2 space-y-2 text-[15px] leading-relaxed text-[#3d3d3d]">{children}</div>
    </section>
  );
}

const TERMS = (
  <>
    <Section n={1} title="The service">
      <p>
        Pins ("the service", "we") is a personal travel journal at pinmy.travel. You can pin places on a map,
        write notes, add photos, organise trips, track your travel passport, and optionally share parts of your
        journal through links you control. The service is provided by the operator of pinmy.travel.
      </p>
    </Section>
    <Section n={2} title="Your account">
      <p>
        You must provide a valid email address and keep your password confidential. You are responsible for
        activity under your account. You must be at least 16 years old (or the age of digital consent where you
        live) to create an account.
      </p>
    </Section>
    <Section n={3} title="Your content">
      <p>
        Everything you pin — titles, notes, photos, trips — remains yours. By uploading content you grant us the
        limited licence needed to store it, display it back to you, and (only when you enable sharing) display it
        to people who hold your share links. We never sell your content or use it for advertising.
      </p>
      <p>
        Don't upload content that is unlawful, that infringes someone else's rights, or that you don't have
        permission to share. We may remove content or suspend accounts that break these rules.
      </p>
    </Section>
    <Section n={4} title="Sharing and collaboration">
      <p>
        Public trip pages, public passports and live location sharing are off by default and only exist while you
        keep the link enabled. Anyone holding an active link can view what it exposes, and visitors may leave
        anonymous appreciation stamps on shared entries. You can rotate or disable any link at any time, which
        immediately cuts off access. Trip collaborators you invite can add and edit pins on those trips.
      </p>
    </Section>
    <Section n={5} title="Subscriptions and billing">
      <p>
        Pins offers free and paid plans (individual and family). Payments are processed by Stripe; we never see
        your full card details. Subscriptions renew automatically until cancelled through the billing portal.
        Cancelling stops future charges; access to paid features continues to the end of the paid period. Except
        where required by law, payments are non-refundable.
      </p>
    </Section>
    <Section n={6} title="Acceptable use">
      <p>
        Don't abuse the service: no attempts to break authentication, scrape other users' data, spam reaction or
        analytics endpoints, or use Pins to harass or track anyone without their knowledge. Rate limits and
        security controls exist to protect everyone.
      </p>
    </Section>
    <Section n={7} title="Termination">
      <p>
        You can delete your account yourself at any time from Settings, which permanently removes your journal.
        We may suspend or terminate accounts that violate these terms, with notice where practicable.
      </p>
    </Section>
    <Section n={8} title="Disclaimers and liability">
      <p>
        Pins is provided "as is" without warranties of any kind. Map data comes from third-party providers and may
        be inaccurate. To the maximum extent permitted by law, our total liability for any claim is limited to the
        amount you paid us in the twelve months before the claim arose.
      </p>
    </Section>
    <Section n={9} title="Changes to these terms">
      <p>
        We may update these terms as the service evolves. Material changes will be announced in the app or by
        email. Continuing to use Pins after changes take effect means you accept the updated terms.
      </p>
    </Section>
    <Section n={10} title="Contact">
      <p>
        Questions about these terms:{" "}
        <a href="mailto:hello@pinmy.travel" className="underline underline-offset-4 text-[#2c4a3e]">
          hello@pinmy.travel
        </a>
        .
      </p>
    </Section>
  </>
);

const PRIVACY = (
  <>
    <Section n={1} title="What we collect">
      <p>
        <strong>Account data:</strong> name, email address, hashed password, plan status, and the date you
        accepted our terms. <strong>Journal data:</strong> the pins, notes, photos, trips, dates and locations you
        choose to record. <strong>Payment data:</strong> handled by Stripe — we store only subscription status and
        customer references, never card numbers. <strong>Minimal analytics:</strong> anonymous counters for our
        own signup prompts (shown/clicked/signed-up) and a server error log; no third-party trackers, no ad
        networks, no fingerprinting.
      </p>
    </Section>
    <Section n={2} title="How we use it">
      <p>
        To run your journal, sync it across devices, process subscriptions, send transactional email
        (verification, password resets) and — only if you keep it switched on — a weekly digest of your own
        activity. You can turn the digest off in Settings at any time. We never sell personal data.
      </p>
    </Section>
    <Section n={3} title="Photos and storage">
      <p>
        Photos are stored in private object storage and served through short-lived signed URLs. They are only
        visible to you, collaborators on the same trip, and — if you enable public sharing — holders of your
        share links.
      </p>
    </Section>
    <Section n={4} title="Sharing controls">
      <p>
        Nothing is public by default. Public trip pages expose only what the page shows (no email address, no
        private notes on hidden pins); public passports expose only country/state stamps and counts. Disabling or
        rotating a link immediately revokes access. Live location sharing stops the moment you end it or it
        expires.
      </p>
    </Section>
    <Section n={5} title="Cookies">
      <p>
        We use a single essential authentication cookie to keep you signed in. No advertising or cross-site
        tracking cookies.
      </p>
    </Section>
    <Section n={6} title="Service providers">
      <p>
        We share the minimum necessary with: Stripe (payments), Resend (transactional email), our hosting and
        object-storage providers, and OpenStreetMap/tile providers (map requests include your viewport, as with
        any web map). AI trip recaps send trip stats and pin titles — never your photos or email — to our AI
        provider to compose the text.
      </p>
    </Section>
    <Section n={7} title="Your rights">
      <p>
        You can export everything (Settings → "Download full archive") and delete your account and all data
        yourself (Settings → Danger zone). You may also email us to access, correct or erase your data. Depending
        on where you live (e.g. GDPR, CCPA) you may have additional statutory rights, which we honour.
      </p>
    </Section>
    <Section n={8} title="Retention and security">
      <p>
        Data is kept while your account exists and deleted when you delete it (server error logs auto-expire after
        30 days). Passwords are stored hashed, share tokens are unguessable, and public endpoints are
        rate-limited.
      </p>
    </Section>
    <Section n={9} title="Changes and contact">
      <p>
        We'll announce material changes to this policy in the app or by email. Privacy questions:{" "}
        <a href="mailto:hello@pinmy.travel" className="underline underline-offset-4 text-[#2c4a3e]">
          hello@pinmy.travel
        </a>
        .
      </p>
    </Section>
  </>
);

export default function Legal({ kind }) {
  const title = kind === "terms" ? "Terms of Service" : "Privacy Policy";
  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#1f1f1f]">
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid={`legal-${kind}`}>
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#5c5c5c] hover:text-[#1f1f1f]"
          data-testid="legal-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} /> Back to Pins
        </Link>
        <p className="overline text-[#b85c47] mt-8">pinmy.travel</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2">{title}</h1>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8a8272] mt-3">
          Effective {EFFECTIVE}
        </p>
        <div className="tactile-card p-6 sm:p-8 mt-8">
          {kind === "terms" ? TERMS : PRIVACY}
          <p className="mt-10 pt-4 border-t border-[#d5cfc1] font-mono text-[9px] uppercase tracking-[0.15em] text-[#8a8272]">
            Also read: {kind === "terms" ? (
              <Link to="/privacy" className="underline underline-offset-2 text-[#2c4a3e]" data-testid="legal-cross-link">Privacy Policy</Link>
            ) : (
              <Link to="/terms" className="underline underline-offset-2 text-[#2c4a3e]" data-testid="legal-cross-link">Terms of Service</Link>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
